/**
 * Zero-pads the numeric portion of a flat number string up to 3 digits (e.g. "1" -> "001", "A-1" -> "A-001", "16" -> "016").
 */
export const padFlatNumber = (flatStr: any): string => {
    if (flatStr === null || flatStr === undefined) return "";
    const str = String(flatStr).trim();
    if (!str || str === "-" || str === "null" || str === "undefined") return str;

    const regex = /^([A-Za-z0-9\s#._-]*?[^0-9])?([0-9]+)([A-Za-z]*)$/;
    const match = str.match(regex);
    if (match) {
        const prefix = match[1] || "";
        const digits = match[2];
        const suffix = match[3] || "";
        if (digits.length < 3) {
            return `${prefix}${digits.padStart(3, "0")}${suffix}`;
        }
    }
    return str;
};

/**
 * Utility functions for Staff Quarters module
 */

export const formatFlatNoWithBlock = (flatData: any, fallbackBlock?: any, rootData?: any): string => {
    if (!flatData) return "-";

    const rawFlatNo = (typeof flatData === "string" || typeof flatData === "number")
        ? String(flatData).trim()
        : (
            flatData.flatNumber ||
            flatData.flatNo ||
            flatData.flat_number ||
            flatData.flat_no ||
            flatData.flatId ||
            flatData.flat_id ||
            flatData.flatName ||
            flatData.flat_name ||
            flatData.flatCode ||
            flatData.flat_code ||
            (typeof flatData.flat === "string" || typeof flatData.flat === "number" ? String(flatData.flat) : "") ||
            flatData.flatDetails?.flatNumber ||
            flatData.flatDetails?.flatNo ||
            flatData.flatDetails?.flat_number ||
            flatData.flatDetails?.flat_no ||
            flatData.flatDetails?.flatId ||
            flatData.flatDetails?.flat_id ||
            flatData.flatDetails?.flatName ||
            flatData.flatDetails?.flat_name ||
            (typeof flatData.flatDetails?.flat === "string" || typeof flatData.flatDetails?.flat === "number" ? String(flatData.flatDetails.flat) : "") ||
            flatData.flat?.flatNumber ||
            flatData.flat?.flatNo ||
            flatData.flat?.flat_number ||
            flatData.flat?.flat_no ||
            flatData.flat?.flatId ||
            flatData.flat?.flat_id ||
            flatData.flat?.flatName ||
            flatData.flat?.flat_name ||
            flatData.allottedFlatNumber ||
            flatData.allottedFlatNo ||
            flatData.allotmentDetails?.flatNumber ||
            flatData.allotmentDetails?.flatNo ||
            flatData.allotmentDetails?.flat_no ||
            flatData.allotmentDetails?.flat_number ||
            ""
        );

    if (!rawFlatNo || rawFlatNo === "-" || rawFlatNo === "null" || rawFlatNo === "undefined") {
        return "-";
    }

    const cleanFlat = String(rawFlatNo).trim();

    // Find block from flatData, fallbackBlock, or rootData
    let block: any = "";
    if (typeof flatData === "object" && flatData !== null) {
        block = flatData.block ??
            flatData.blockName ??
            flatData.block_name ??
            flatData.flatDetails?.block ??
            flatData.flatDetails?.blockName ??
            flatData.flatDetails?.block_name ??
            flatData.flat?.block ??
            flatData.flat?.blockName ??
            flatData.flat?.block_name ??
            flatData.inventoryDetails?.block ??
            flatData.inventoryDetails?.blockName ??
            flatData.inventoryDetails?.block_name ??
            flatData.inventory?.block ??
            flatData.inventory?.blockName ??
            flatData.inventory?.block_name ??
            flatData.allotmentDetails?.block ??
            flatData.allotmentDetails?.blockName ??
            flatData.allotmentDetails?.block_name ??
            flatData.registration?.block ??
            flatData.registration?.blockName ??
            "";
    }

    if ((block === "" || block === null || block === undefined) && fallbackBlock !== undefined && fallbackBlock !== null) {
        block = typeof fallbackBlock === "object" && fallbackBlock !== null
            ? (fallbackBlock.block ?? fallbackBlock.blockName ?? fallbackBlock.block_name ?? fallbackBlock.flatDetails?.block ?? fallbackBlock.flat?.block ?? fallbackBlock.inventoryDetails?.block ?? fallbackBlock.inventory?.block ?? "")
            : String(fallbackBlock);
    }

    if ((block === "" || block === null || block === undefined) && rootData && typeof rootData === "object") {
        const willingness = Array.isArray(rootData.willingnessDetails)
            ? rootData.willingnessDetails[0]
            : rootData.willingnessDetails;

        block = rootData.block ??
            rootData.blockName ??
            rootData.block_name ??
            rootData.allotmentDetails?.block ??
            rootData.allotmentDetails?.blockName ??
            rootData.allotmentDetails?.block_name ??
            rootData.allotmentDetails?.flatDetails?.block ??
            rootData.allotmentDetails?.flat?.block ??
            willingness?.block ??
            willingness?.blockName ??
            willingness?.block_name ??
            willingness?.flatDetails?.block ??
            willingness?.flat?.block ??
            rootData.inventoryDetails?.block ??
            rootData.inventoryDetails?.blockName ??
            rootData.inventory?.block ??
            rootData.inventory?.blockName ??
            rootData.flatDetails?.block ??
            rootData.flatDetails?.blockName ??
            rootData.flat?.block ??
            rootData.flat?.blockName ??
            "";
    }

    const cleanBlock = block !== undefined && block !== null && String(block).trim() !== "" && String(block).trim() !== "-" && String(block).trim() !== "null" && String(block).trim() !== "undefined"
        ? String(block).trim()
        : "";

    if (cleanBlock) {
        // If cleanFlat already starts with cleanBlock (e.g. "5-601" when block is 5), return formatted with padded unit
        if (cleanFlat.toLowerCase().startsWith(cleanBlock.toLowerCase() + "-")) {
            const parts = cleanFlat.split("-");
            const unit = parts.slice(1).join("-").trim();
            return `${cleanBlock}-${padFlatNumber(unit)}`;
        }

        // If cleanFlat has another prefix (e.g. "C-601"), extract the unit number "601" and prepend the actual block "5" -> "5-601"
        const parts = cleanFlat.split("-");
        if (parts.length > 1) {
            const unitNumber = parts[parts.length - 1].trim();
            if (unitNumber) {
                return `${cleanBlock}-${padFlatNumber(unitNumber)}`;
            }
        }

        return `${cleanBlock}-${padFlatNumber(cleanFlat)}`;
    }

    return padFlatNumber(cleanFlat);
};

export const resolveFlatCategory = (dataObj: any, fallbackBlock?: any, rootData?: any): string => {
    if (!dataObj && !fallbackBlock && !rootData) return "-";

    const candidateKeys = [
        "flatCategory",
        "flat_category",
        "category",
        "categoryName",
        "category_name",
        "categoryCode",
        "category_code",
        "flatCategoryName",
        "flat_category_name",
        "quarterCategory",
        "quarter_category"
    ];

    const searchObj = (obj: any): string | null => {
        if (!obj || typeof obj !== "object") return null;
        for (const key of candidateKeys) {
            const val = obj[key];
            if (val !== undefined && val !== null && String(val).trim() !== "" && String(val).trim() !== "null" && String(val).trim() !== "undefined") {
                return String(val).trim();
            }
        }
        return null;
    };

    const searchDeep = (obj: any, depth = 0): string | null => {
        if (!obj || depth > 3) return null;
        if (typeof obj === "string" || typeof obj === "number" || typeof obj === "boolean") return null;

        if (Array.isArray(obj)) {
            for (const item of obj) {
                const res = searchDeep(item, depth + 1);
                if (res) return res;
            }
            return null;
        }

        const direct = searchObj(obj);
        if (direct) return direct;

        const subObjects = [
            obj.willingnessDetails,
            obj.flatDetails,
            obj.flat,
            obj.inventoryDetails,
            obj.inventory,
            obj.allotmentDetails,
            obj.allotment,
            obj.registration,
            obj.registrationDetails,
            obj.occupationDetails,
            obj.financeDetails,
            obj.vacationIntimationDetails,
            obj.items,
            obj.data,
            obj.result
        ];

        for (const sub of subObjects) {
            if (sub) {
                const res = searchDeep(sub, depth + 1);
                if (res) return res;
            }
        }

        return null;
    };

    let found = searchDeep(dataObj);
    if (found) return found;

    if (fallbackBlock) {
        found = searchDeep(fallbackBlock);
        if (found) return found;
    }

    if (rootData) {
        found = searchDeep(rootData);
        if (found) return found;
    }

    return "-";
};

export const resolveFlatPool = (dataObj: any, fallbackBlock?: any, rootData?: any): string => {
    if (!dataObj && !fallbackBlock && !rootData) return "-";

    const candidateKeys = [
        "poolName",
        "pool_name",
        "pool",
        "poolType",
        "pool_type",
        "subPool",
        "sub_pool",
        "flatPool",
        "flat_pool"
    ];

    const searchObj = (obj: any): string | null => {
        if (!obj || typeof obj !== "object") return null;
        for (const key of candidateKeys) {
            const val = obj[key];
            if (val !== undefined && val !== null) {
                if (typeof val === "string" || typeof val === "number") {
                    const s = String(val).trim();
                    if (s && s !== "null" && s !== "undefined" && s !== "[object Object]") return s;
                } else if (typeof val === "object") {
                    const nestedName = val.name || val.poolName || val.pool_name || val.subPool || val.sub_pool || val.title || val.code;
                    if (typeof nestedName === "string" || typeof nestedName === "number") {
                        const s = String(nestedName).trim();
                        if (s && s !== "null" && s !== "undefined") return s;
                    }
                }
            }
        }
        return null;
    };

    const searchDeep = (obj: any, depth = 0): string | null => {
        if (!obj || depth > 3) return null;
        if (typeof obj === "string" || typeof obj === "number" || typeof obj === "boolean") return null;

        if (Array.isArray(obj)) {
            for (const item of obj) {
                const res = searchDeep(item, depth + 1);
                if (res) return res;
            }
            return null;
        }

        const direct = searchObj(obj);
        if (direct) return direct;

        const subObjects = [
            obj.flatDetails,
            obj.flat,
            obj.inventoryDetails,
            obj.inventory,
            obj.allotmentDetails,
            obj.allotment,
            obj.registration,
            obj.registrationDetails,
            obj.willingnessDetails,
            obj.occupationDetails,
            obj.financeDetails,
            obj.items,
            obj.data,
            obj.result
        ];

        for (const sub of subObjects) {
            if (sub) {
                const res = searchDeep(sub, depth + 1);
                if (res) return res;
            }
        }

        return null;
    };

    let found = searchDeep(dataObj);
    if (found) return found;

    if (fallbackBlock) {
        found = searchDeep(fallbackBlock);
        if (found) return found;
    }

    if (rootData) {
        found = searchDeep(rootData);
        if (found) return found;
    }

    return "-";
};


